import { Typography } from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { ExpenseType } from 'further-types/lp/income-and-expenses';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import { IncomeAndExpensesLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFormContext } from 'react-hook-form';
import { CapitalActionFormValues } from '../../../hooks/useCapitalActionForm';
import CapitalCallAllocations from '../CapitalCallAllocations';
import useActionAllocationsChangeHandler from './hooks/useActionAllocationsChangeHandler';

type Props = {
  vehicleId: string;
  entryIndex: number;
  baseCurrency: CurrencyCode;
  expenses: Array<{
    id: string;
    type: ExpenseType;
    counterpartyLabel: string;
    uncalledAmountLcy: number;
    date: CalendarDay;
    allocations: Array<{
      investorLabel: string;
      commitmentId: string;
      totalCommitmentLcy: number;
      unallocatedCommitmentLcy: number;
      allocationAmountTcy: number;
      allocationPercentage: number;
      excludeFromAllocation: boolean;
      closeDate: Date;
      paidAmountLcy: number;
    }>;
  }>;
};

const ExpensesDetails: React.FC<Props> = ({
  expenses,
  baseCurrency,
  vehicleId,
  entryIndex,
}) => {
  const { watch } = useFormContext<CapitalActionFormValues>();

  const actions = watch(`entries.${entryIndex}.actions`);

  const handleAllocationsChange = useActionAllocationsChangeHandler(entryIndex);

  return (
    <Column spacing="lg">
      {expenses.map((expense, index) => (
        <Column key={expense.id} spacing="sm">
          <Column spacing="sm">
            <Typography variant="h5">
              Expense {index + 1} - {expense.counterpartyLabel} (
              {expense.date.toReadable()})
            </Typography>
            <StaticField label="Counterparty" spacing="xxs">
              {expense.counterpartyLabel}
            </StaticField>
            <StaticField label="Type" spacing="xxs">
              {IncomeAndExpensesLabels[expense.type] ?? expense.type}
            </StaticField>
            <StaticField label="Amount" spacing="xxs">
              {numberToCurrencyString(expense.uncalledAmountLcy, {
                currency: baseCurrency,
              })}
            </StaticField>
            <StaticField label="Date" spacing="xxs">
              {expense.date.toReadable()}
            </StaticField>
          </Column>
          <CapitalCallAllocations
            entryIndex={entryIndex}
            title="Expense allocation"
            calledFundsLabel="Expense capital call"
            currency={baseCurrency}
            actionId={expense.id}
            allocationRule={AllocationRule.LinkedAction}
            capitalCalledLcy={expense.uncalledAmountLcy}
            vehicleId={vehicleId}
            capitalBreakdown={
              actions.find((action) => action.id === expense.id)
                ?.capitalBreakdown ?? []
            }
            keepCallAmountFixed={true}
            onChange={(capitalBreakdown) => {
              handleAllocationsChange(expense.id, capitalBreakdown);
            }}
          />
        </Column>
      ))}
    </Column>
  );
};

export default ExpensesDetails;
